import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './onboardingTour.css';

export const TOUR_PENDING_KEY = 'pendingTour';
export const DUP_MERGE_EVENT  = 'tour:open-dup-merge';

const SHOW_BUTTONS = ['next', 'previous', 'close'];

// ── Full What's New tour (3 stages) ─────────────────────────────────────────

export function startTour(history, setMenuOpen) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        popover: {
          title: 'New Navigation Menu (1 of 3)',
          description:
            'Your navigation menu is now open in the top-right corner. All navigation has moved into this single menu — Product Search, Admin tools, your account, and more — all organized into sections.',
        },
      },
      {
        element: '#tour-header-search',
        onHighlightStarted: () => setMenuOpen(false),
        popover: {
          title: 'Banner Search Bar (1 of 3)',
          description:
            'The search bar in the banner lets you search your product catalog from anywhere in the app without leaving the current page.',
          side: 'bottom',
          nextBtnText: 'Next: QR Styling →',
          onNextClick: () => {
            d.destroy();
            sessionStorage.setItem(TOUR_PENDING_KEY, 'qr');
            history.push('/settings');
          },
        },
      },
    ],
  });
  setMenuOpen(true);
  d.drive();
}

export function resumeQrStage(history) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-qr-settings',
        popover: {
          title: 'QR Code Settings (2 of 3)',
          description:
            'All QR code configuration lives here. Create named style presets that users can choose at print time.',
          side: 'right',
        },
      },
      {
        element: '#tour-qr-styles-btn',
        popover: {
          title: 'Edit QR Styles (2 of 3)',
          description:
            'Click "Edit QR Styles" to open the style editor where you can create and manage style presets.',
          side: 'bottom',
          nextBtnText: 'Open Style Editor →',
          onNextClick: () => {
            d.destroy();
            document.getElementById('tour-qr-styles-btn')?.click();
            setTimeout(() => startQrStyleSubTour(history), 400);
          },
        },
      },
    ],
  });
  d.drive();
}

function startQrStyleSubTour(history) {
  let d2;
  d2 = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    overlayOpacity: 0,
    steps: [
      {
        popover: {
          title: 'QR Style: Appearance (2 of 3)',
          description:
            'Set the QR color and background, pick from six dot styles and corner shapes, upload a logo to appear in the center, and choose which product fields print as label text above and below the QR code. A live preview updates in real time.',
        },
      },
      {
        popover: {
          title: 'QR Style: Price Color Rules (2 of 3)',
          description:
            'Scroll down to "Price Colors" to add rules that automatically change a QR code\'s color based on the product\'s price range — so budget, mid-range, and premium products each show a different colored label.',
          nextBtnText: 'Next: Duplicate Detection →',
          onNextClick: () => {
            d2.destroy();
            resumeDuplicateStage(history);
          },
        },
      },
    ],
  });
  d2.drive();
}

// Stage 3 entry — navigates to /tour/products, driver runs on mount
export function resumeDuplicateStage(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'tour-dup-product');
  history.push('/tour/products');
}

// ── Dup warning driver — called from TourProductsPage on mount ───────────────
// afterDone: callback to run after the merge dialog tour ends (null = go to /home)

export function driverDupWarning(history, afterDone) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-dup-warning-icon',
        popover: {
          title: 'Duplicate Detection (3 of 3)',
          description:
            'When a product has a potential duplicate in the catalog, an orange warning icon appears in the product list. The tooltip shows the match confidence. Click the icon to review the two products side by side.',
          side: 'right',
          nextBtnText: 'Review this duplicate →',
          onNextClick: () => {
            d.destroy();
            window.dispatchEvent(new CustomEvent(DUP_MERGE_EVENT, { detail: { afterDone } }));
          },
        },
      },
    ],
  });
  d.drive();
}

// ── Merge dialog driver — called from TourProductsPage after dialog opens ────
// afterDone is always provided (TourProductsPage wraps it to also close the dialog)

export function driverDupMerge(history, afterDone) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    overlayOpacity: 0,
    steps: [
      {
        popover: {
          title: 'Review Both Products (3 of 3)',
          description:
            'Both versions of the product are shown side by side with a match score for each field. Click "Keep this record" on the version you want to preserve — usually the one with the most complete data or the most recent update.',
        },
      },
      {
        popover: {
          title: 'Merge or Dismiss (3 of 3)',
          description:
            'Once you have selected a record to keep, click Confirm Merge to combine them into a single product. If these are actually different products, click Not a Duplicate to dismiss the match without making any changes.',
          nextBtnText: 'Continue Tour →',
          onNextClick: () => {
            d.destroy();
            afterDone();
          },
        },
      },
    ],
  });
  d.drive();
}

// ── Printed-indicator driver — called from TourProductsPage on mount ─────────

export function driverPrintedIndicator(history) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: ['next', 'close'],
    steps: [
      {
        element: '#tour-printed-icon',
        popover: {
          title: 'Printed Product Indicator',
          description:
            'Products that have had a QR code label printed show a blue printer icon next to their name. Hover over it to confirm a label has been generated — so your team always knows which items are already tagged on the floor.',
          side: 'right',
          nextBtnText: 'Got it',
          onNextClick: () => {
            d.destroy();
            history.push('/products');
          },
        },
      },
    ],
  });
  d.drive();
}

// ── Individual feature tours (from What's New modal "Tour →" buttons) ────────

export function startNavTour(history, setMenuOpen) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: ['next', 'close'],
    steps: [
      {
        popover: {
          title: 'New Navigation Menu',
          description:
            'Your navigation menu is now in the top-right corner. Product Search is featured at the top, and everything else is organized into Navigate, Admin, and Account sections.',
          nextBtnText: 'Got it',
          onNextClick: () => { d.destroy(); setMenuOpen(false); },
        },
      },
    ],
  });
  setMenuOpen(true);
  d.drive();
}

export function startHeaderSearchTour(history, setMenuOpen) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: ['next', 'close'],
    steps: [
      {
        element: '#tour-header-search',
        onHighlightStarted: () => setMenuOpen(false),
        popover: {
          title: 'Banner Search Bar',
          description:
            'The search bar in the top banner lets you search your product catalog from anywhere in the app without navigating away from the current page.',
          side: 'bottom',
          nextBtnText: 'Got it',
          onNextClick: () => d.destroy(),
        },
      },
    ],
  });
  d.drive();
}

export function startQrTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'qr');
  history.push('/settings');
}

export function resumePrintedIndicatorTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'tour-printed');
  history.push('/tour/products');
}

export function startDuplicatesTour(history) {
  resumeDuplicateStage(history);
}

// ── Scan Dashboard tour (separate upcoming release — not part of the tour above) ──
// Started automatically when /insights mounts with pendingTour = 'scandashboard'.
export function resumeScanDashboardStage(history) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-scan-dashboard',
        popover: {
          title: 'Scan Dashboard',
          description:
            'The new Scan Dashboard puts all of your QR scan analytics on one page. Use the filter bar at the top to set the date range, audience (customers vs staff), location, and whether to show only products with a printed QR label (on by default) — every panel below updates together. You get KPIs with trends, a view → like → checkout funnel per product, an activity heatmap, manufacturer share, rising/falling products, and a list of products that haven\'t been scanned yet.',
          side: 'bottom',
          nextBtnText: 'Finish Tour',
          onNextClick: () => {
            d.destroy();
            sessionStorage.setItem(TOUR_PENDING_KEY, 'tour-complete');
            history.push('/home');
          },
        },
      },
    ],
  });
  d.drive();
}

// ── 4.8 release tours ──────────────────────────────────────────────────────

// Opens the nav menu and highlights the Feature Requests item; "Take me there"
// lands on the page itself. Menu needs a beat to render before highlighting.
export function startFeatureRequestsTour(history, setMenuOpen) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: ['next', 'close'],
    steps: [
      {
        element: '#tour-feature-requests',
        popover: {
          title: 'Feature Requests',
          description:
            'Have an idea for Showroom Pricing? Vote on features other customers have requested, join the discussion in comments, or submit your own — and watch where each request stands.',
          side: 'left',
          nextBtnText: 'Take me there',
          onNextClick: () => {
            d.destroy();
            setMenuOpen(false);
            history.push('/feature-requests');
          },
        },
      },
    ],
  });
  setMenuOpen(true);
  setTimeout(() => d.drive(), 300);
}

export function startHelpCatalogTour(history, setMenuOpen) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: ['next', 'close'],
    steps: [
      {
        element: '#tour-help',
        popover: {
          title: 'Help Video Catalog',
          description:
            'The Help page now has a searchable library of walkthrough videos. Type what you\'re trying to do and the right video surfaces instantly — new videos appear as they\'re published.',
          side: 'left',
          nextBtnText: 'Take me there',
          onNextClick: () => {
            d.destroy();
            setMenuOpen(false);
            history.push('/about');
          },
        },
      },
    ],
  });
  setMenuOpen(true);
  setTimeout(() => d.drive(), 300);
}

// Navigates to Settings; resumeTimezoneStage runs when the page mounts with
// pendingTour = 'timezone' (same pattern as the QR tour).
export function startTimezoneTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'timezone');
  history.push('/settings');
}

export function resumeTimezoneStage(history) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: ['next', 'close'],
    steps: [
      {
        element: '#tour-timezone-setting',
        popover: {
          title: 'Company Default Timezone',
          description:
            'Timestamps across the app are shown in this timezone for everyone who hasn\'t chosen their own. Each user can pick a personal timezone on their profile page.',
          side: 'top',
          nextBtnText: 'Got it',
          onNextClick: () => d.destroy(),
        },
      },
    ],
  });
  d.drive();
}
